import { Formik } from "formik";
import { CustomInput, CustomSelect } from "@/components";
import { Button } from "@/components/ui/button";
import { addSingleTeacher, editTeacher } from "@/api/admin";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2Icon } from "lucide-react";
import { showSuccessToast } from "@/lib/toast";
import { Teacher } from "@/types/user.types";
import * as Yup from "yup";

type AddSingleInstructorProps = {
  onClose?: () => void;
  currentInstructor?: Teacher;
};

const AddSingleInstructor = ({
  onClose,
  currentInstructor,
}: AddSingleInstructorProps) => {
  const queryClient = useQueryClient();

  const initialValues = currentInstructor?.id
    ? {
      id: currentInstructor?.id || "",
      surname: currentInstructor?.surname || "",
      title: currentInstructor?.title || "",
      firstname: currentInstructor?.firstname || "",
      middlename: currentInstructor?.middlename || "",
      gender: currentInstructor?.gender || "",
      email: currentInstructor?.email || "",
      mobile: currentInstructor?.mobile || "",
    }
    : {
      surname: "",
      title: "",
      firstname: "",
      middlename: "",
      gender: "",
      email: "",
      mobile: "",
      password: "",
    };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("This field is required"),
    surname: Yup.string().required("This field is required"),
    firstname: Yup.string().required("This field is required"),
    middlename: Yup.string(),
    gender: Yup.string().required("This field is required"),
    email: Yup.string().email().required("This field is required"),
    mobile: Yup.string().required("This field is required"),
    password: Yup.string().when("id", {
      is: (value) => !!value,
      then: (schema) => schema,
      otherwise: (schema) => schema.required("This field is required")
    })
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: currentInstructor?.id ? editTeacher : addSingleTeacher,
    onSuccess: () => {
      showSuccessToast(currentInstructor ? "Teacher updated successfully" : "Teacher added successfully");
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      onClose?.();
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => mutate(values)}
    >
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <form
          className="flex flex-col justify-between gap-5 pt-5"
          onSubmit={handleSubmit}
        >
          <CustomInput
            label="Title"
            id="title"
            value={values.title}
            onChange={handleChange}
            placeholder="Enter title"
            name="title"
          />
          <CustomInput
            label="Surname"
            id="surname"
            value={values.surname}
            onChange={handleChange}
            placeholder="Enter surname"
            name="surname"
          />
          <CustomInput
            label="First Name"
            value={values.firstname}
            id="firstname"
            onChange={handleChange}
            placeholder="Enter first name"
            name="firstname"
          />
          <CustomInput
            label="Middle Name"
            id="middlename"
            value={values.middlename}
            onChange={handleChange}
            placeholder="Enter middle name"
            name="middlename"
          />
          <CustomSelect
            label="Gender"
            value={values.gender}
            options={[
              { label: "Male", value: "male" },
              { label: "Female", value: "female" },
            ]}
            onChange={(val) => setFieldValue("gender", val)}
            placeholder="Select gender"
            name="gender"
          />
          <CustomInput
            label="Email"
            id="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            placeholder="Enter email"
            name="email"
          />
          <CustomInput
            label="Phone"
            id="mobile"
            type="tel"
            value={values.mobile}
            onChange={handleChange}
            placeholder="Enter phone number"
            name="mobile"
          />

          {!currentInstructor?.id && (
            <CustomInput
              label="Password"
              id="password"
              type="password"
              value={values.password}
              onChange={handleChange}
              placeholder="Enter password"
              name="password"
              showPasswordToggle
            />
          )}

          <div className="flex items-center justify-end w-full gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => onClose?.()}
            >
              Cancel
            </Button>

            <Button type="submit" className="min-w-[100px]">
              {isLoading ? <Loader2Icon className="animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddSingleInstructor;
